import React, { useEffect, useState } from "react";
import api, { downloadFile, openFile, fileUrl, uploadImage, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Modal, Field, Input, Select, Empty, Badge } from "@/components/ui-kit";
import { Plus, Trash2, Pencil, Dumbbell, ClipboardList, FileText, Camera, Clock, CheckCircle2, Package, ListChecks, Users, CalendarDays, LayoutList, FileUp, LayoutGrid, Lock, Download, X } from "lucide-react";
import ExerciseSheetModal from "@/components/ExerciseSheetModal";
import { fmtDate } from "@/lib/utils";
import { toast } from "sonner";

const THEMES = ["Coordination", "Conduite de balle", "Contrôle de balle", "Passe", "Frappe", "Collectif", "Échauffement", "Physique", "Gardien"];
const THEME_COLOR = { "Coordination": "yellow", "Conduite de balle": "green", "Contrôle de balle": "blue", "Passe": "primary", "Frappe": "red", "Collectif": "purple", "Échauffement": "yellow", "Physique": "red", "Gardien": "blue" };
const emptyEx = { name: "", theme: "Coordination", duration: 15, description: "", image: "", material: "", tasks: "", group: "" };
const ADMIN_COLORS = { green: "#22c55e", orange: "#f59e0b", red: "#ef4444", blue: "#3b82f6" };

// Grille de terrain 8 zones : chaque zone contient au plus un exercice (drag depuis le cahier)
const ZONE_TINTS = ["bg-primary/70", "bg-sky-500/70", "bg-yellow-500/70", "bg-purple-500/70", "bg-red-500/70", "bg-emerald-500/70", "bg-orange-500/70", "bg-pink-500/70"];
const PitchBoard = ({ zones = {}, exercises, editable, onDropZone, onClearZone, onZoneClick, testid }) => {
  const nameOf = (id) => exercises.find((e) => e.id === id)?.name || "Exercice supprimé";
  const distinct = [...new Set(Object.values(zones))];
  const tintOf = (id) => ZONE_TINTS[distinct.indexOf(id) % ZONE_TINTS.length];
  return (
    <div data-testid={testid} className="w-full">
      <div className="relative rounded-lg overflow-hidden border-2 border-white/70 shadow-inner" style={{ aspectRatio: "5 / 3", background: "linear-gradient(180deg,#16a34a,#15803d)" }}>
        <div className="absolute inset-0 grid grid-cols-4 grid-rows-2">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((z) => {
            const exId = zones[z];
            const clickable = exId && (editable || onZoneClick);
            return (
              <div key={z} data-testid={`board-zone-${z}`}
                onDragOver={editable ? (e) => e.preventDefault() : undefined}
                onDrop={editable ? (e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); if (id) onDropZone(z, id); } : undefined}
                onClick={exId ? (editable ? () => onClearZone(z) : (onZoneClick ? () => onZoneClick(exId) : undefined)) : undefined}
                title={editable && exId ? "Cliquer pour retirer" : (!editable && exId && onZoneClick ? "Voir la fiche de l'exercice" : "")}
                className={`relative border border-white/25 flex items-center justify-center p-1 text-center transition-colors ${exId ? `${tintOf(exId)} text-white ring-1 ring-inset ring-white/60` : "text-white/50 hover:bg-white/10"} ${clickable ? "cursor-pointer hover:brightness-110" : ""}`}>
                {exId ? <span className={`text-[10px] font-bold leading-tight line-clamp-3 ${!editable && onZoneClick ? "underline decoration-white/40" : ""}`}>{nameOf(exId)}</span> : <span className="text-sm font-black">{z}</span>}
              </div>
            );
          })}
        </div>
        <div className="pointer-events-none absolute left-1/2 top-0 bottom-0 w-px bg-white/50 -translate-x-1/2" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 w-10 h-10 rounded-full border border-white/50 -translate-x-1/2 -translate-y-1/2" />
        <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 h-1/3 w-1.5 bg-white/80 rounded-r" />
        <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 h-1/3 w-1.5 bg-white/80 rounded-l" />
      </div>
    </div>
  );
};


export default function Trainings() {
  const { can, hasRole } = useAuth();
  const canManageEx = can("exercises_manage");
  const canExport = can("data_export");
  const canPdf = hasRole("admin") || hasRole("coach");
  const canPlan = hasRole("admin"); // seul l'admin planifie/modifie les séances
  const [tab, setTab] = useState("library");
  const [exercises, setExercises] = useState([]);
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEx);
  const [uploading, setUploading] = useState(false);
  const [eventId, setEventId] = useState("");
  const [pitches, setPitches] = useState([]);
  const [targetDuration, setTargetDuration] = useState(75);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const uid = () => Math.random().toString(36).slice(2);
  const [filter, setFilter] = useState("all");
  const [sessionComment, setSessionComment] = useState("");
  const [sessionNote, setSessionNote] = useState("");
  const [sessionReady, setSessionReady] = useState(false);
  const [recap, setRecap] = useState([]);
  const [sheetEx, setSheetEx] = useState(null);
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dragOverGrp, setDragOverGrp] = useState(null);
  const [grpFilter, setGrpFilter] = useState("all");
  const [eventAttendance, setEventAttendance] = useState([]);

  const loadEx = () => api.get("/exercises").then((r) => setExercises(r.data));
  const loadEvents = () => api.get("/events").then((r) => setEvents(r.data.filter((e) => e.type === "training")));
  useEffect(() => { loadEx(); loadEvents(); api.get("/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (tab === "recap") api.get("/exercises/recap").then((r) => setRecap(r.data.themes || [])).catch(() => {});
  }, [tab]);

  useEffect(() => {
    setEditMode(false);
    if (!eventId) { setPitches([]); setSessionComment(""); setSessionNote(""); setTargetDuration(75); setGroups([]); return; }
    const ev = events.find((e) => e.id === eventId);
    setSessionComment(ev?.session_comment || "");
    setSessionNote(ev?.session_note || "");
    setSessionReady(!!ev?.session_ready);
    setGroups((ev?.session_groups || []).map((g) => ({ label: g.label, member_ids: [...(g.member_ids || [])] })));
    api.get(`/attendance/${eventId}`).then((r) => setEventAttendance(r.data || [])).catch(() => setEventAttendance([]));
    if (ev?.session_pitches && ev.session_pitches.length) {
      setPitches(ev.session_pitches.map((p) => ({ uid: uid(), name: p.name || "Terrain", duration: p.duration || 20, zones: { ...(p.zones || {}) } })));
      setTargetDuration(ev.session_target_duration || ev.session_pitches.reduce((s, p) => s + (p.duration || 0), 0) || 75);
    } else if (ev?.exercise_zones && Object.keys(ev.exercise_zones).length) {
      // Conversion « au mieux » depuis l'ancien format : placer chaque exercice dans ses zones enregistrées
      const zones = {};
      Object.entries(ev.exercise_zones).forEach(([exId, zs]) => (zs || []).forEach((z) => { zones[z] = exId; }));
      const dur = Object.values(ev.exercise_durations || {}).reduce((s, d) => s + (d || 0), 0) || 60;
      setPitches([{ uid: uid(), name: "Terrain 1", duration: dur, zones }]);
      setTargetDuration(dur || 75);
    } else {
      setPitches([]);
      setTargetDuration(75);
    }
  }, [eventId, events]);

  const savedSession = (() => {
    const ev = events.find((e) => e.id === eventId);
    return ev && ((ev.session_pitches && ev.session_pitches.length) || (ev.exercise_ids && ev.exercise_ids.length));
  })();
  const readOnly = !canPlan || (savedSession && !editMode);

  const openNew = () => { setEditing(null); setForm(emptyEx); setOpen(true); };
  const openEdit = (ex) => { setEditing(ex); setForm({ ...emptyEx, ...ex }); setOpen(true); };

  // --- Terrains ---
  const addPitch = () => setPitches((ps) => {
    const used = ps.reduce((s, p) => s + (parseInt(p.duration) || 0), 0);
    const remain = Math.max((parseInt(targetDuration) || 0) - used, 0);
    return [...ps, { uid: uid(), name: `Terrain ${ps.length + 1}`, duration: remain > 0 ? remain : 20, zones: {} }];
  });
  const removePitch = (u) => setPitches((ps) => ps.filter((p) => p.uid !== u).map((p, i) => ({ ...p, name: p.name?.startsWith("Terrain ") ? `Terrain ${i + 1}` : p.name })));
  const setPitchDuration = (u, val) => setPitches((ps) => ps.map((p) => p.uid === u ? { ...p, duration: val } : p));
  const dropOnZone = (u, zone, exId) => setPitches((ps) => ps.map((p) => p.uid === u ? { ...p, zones: { ...p.zones, [zone]: exId } } : p));
  const clearZone = (u, zone) => setPitches((ps) => ps.map((p) => { if (p.uid !== u) return p; const z = { ...p.zones }; delete z[zone]; return { ...p, zones: z }; }));

  const distinctOf = (p) => [...new Set(Object.values(p.zones || {}))];
  const pitchesTotal = pitches.reduce((s, p) => s + (parseInt(p.duration) || 0), 0);

  // --- Groupes de joueurs (A, B, C...) ---
  const memberById = (id) => members.find((m) => m.id === id);
  const nextGroupLabel = () => String.fromCharCode(65 + groups.length);
  const addGroup = () => setGroups((g) => [...g, { label: nextGroupLabel(), member_ids: [] }]);
  const removeGroup = (i) => setGroups((g) => g.filter((_, idx) => idx !== i));
  const renameGroup = (i, label) => setGroups((g) => g.map((grp, idx) => (idx === i ? { ...grp, label } : grp)));
  const addMemberToGroup = (i, mid) => { if (!mid) return; setGroups((g) => g.map((grp, idx) => (idx === i && !grp.member_ids.includes(mid) ? { ...grp, member_ids: [...grp.member_ids, mid] } : grp))); };
  const removeMemberFromGroup = (i, mid) => setGroups((g) => g.map((grp, idx) => (idx === i ? { ...grp, member_ids: grp.member_ids.filter((x) => x !== mid) } : grp)));
  const yearOf = (m) => { const bd = String(m?.birthdate || ""); if (/^\d{4}/.test(bd)) return bd.slice(0, 4); return /^\d{4}$/.test(String(m?.category || "")) ? m.category : ""; };
  const moveMember = (mid, to) => setGroups((g) => g.map((grp, idx) => {
    const ids = grp.member_ids.filter((x) => x !== mid);
    return { ...grp, member_ids: idx === to ? [...ids, mid] : ids };
  }));
  const dragProps = (mid) => (readOnly ? {} : { draggable: true, onDragStart: (e) => { e.dataTransfer.setData("application/x-member", mid); e.dataTransfer.effectAllowed = "move"; } });
  const dropProps = (to) => (readOnly ? {} : {
    onDragOver: (e) => { if (e.dataTransfer.types.includes("application/x-member")) { e.preventDefault(); setDragOverGrp(to); } },
    onDragLeave: () => setDragOverGrp(null),
    onDrop: (e) => { e.preventDefault(); setDragOverGrp(null); const mid = e.dataTransfer.getData("application/x-member"); if (mid) moveMember(mid, to); },
  });
  const confirmedIds = new Set(eventAttendance.filter((r) => r.parent_declared === "present" || r.status === "present").map((r) => r.member_id));
  const assignedIds = new Set(groups.flatMap((g) => g.member_ids));
  const availableForGroup = () => members.filter((m) => confirmedIds.has(m.id) && !assignedIds.has(m.id) && (grpFilter === "all" || m.group === grpFilter));

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, duration: form.duration ? parseInt(form.duration) : null };
    if (editing) await api.put(`/exercises/${editing.id}`, payload);
    else await api.post("/exercises", payload);
    toast.success(editing ? "Exercice mis à jour" : "Exercice créé");
    setOpen(false); loadEx();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet exercice ?")) return;
    await api.delete(`/exercises/${id}`); toast.success("Exercice supprimé"); loadEx();
  };

  const onImg = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const path = await uploadImage(file); setForm((f) => ({ ...f, image: path })); toast.success("Schéma ajouté"); }
    catch { toast.error("Échec de l'upload"); } finally { setUploading(false); }
  };

  const [importing, setImporting] = useState(false);
  const onImportPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    const tid = toast.loading("Analyse du PDF et extraction des exercices...");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/exercises/import-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.count} exercice(s) importé(s) depuis le PDF`, { id: tid });
      loadEx();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Échec de l'import du PDF", { id: tid });
    } finally { setImporting(false); }
  };

  const pick = (list, n) => {
    const shuffled = [...list].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, n).map((x) => x.id);
  };
  const generateTypical = () => {
    const has = (ex, ...kw) => kw.some((k) => (ex.theme || "").toLowerCase().includes(k) || (ex.name || "").toLowerCase().includes(k));
    const warmup = exercises.filter((ex) => has(ex, "coordination", "réaction", "échauffement"));
    const games = exercises.filter((ex) => has(ex, "festifoot", "duel", "dribble"));
    const shooting = exercises.filter((ex) => has(ex, "tir", "frappe", "précision"));
    const usedThemes = new Set();
    const technique = exercises.filter((ex) => has(ex, "conduite", "contrôle", "passe") && !has(ex, "festifoot", "duel"));
    const sel = new Set();
    pick(warmup, 1).forEach((id) => sel.add(id));
    for (const ex of [...technique].sort(() => Math.random() - 0.5)) {
      if (sel.size >= 4) break;
      if (usedThemes.has(ex.theme)) continue;
      usedThemes.add(ex.theme); sel.add(ex.id);
    }
    pick(shooting, 1).forEach((id) => sel.add(id));
    pick(games, 1).forEach((id) => sel.add(id));
    const ids = [...sel].filter(Boolean);
    if (!ids.length) { toast.error("Aucun exercice disponible pour générer une séance"); return; }
    const zones = {};
    ids.slice(0, 8).forEach((id, i) => { zones[i + 1] = id; });
    setPitches([{ uid: uid(), name: "Terrain 1", duration: parseInt(targetDuration) || 60, zones }]);
    toast.success(`Séance type générée (${Object.keys(zones).length} exercices) — placez/ajustez puis enregistrez`);
  };

  const saveSession = async (ready = null) => {
    setSaving(true);
    const readyVal = ready === null ? sessionReady : ready;
    try {
      const session_pitches = pitches.map((p) => ({ name: p.name, duration: parseInt(p.duration) || 0, zones: p.zones || {} }));
      const hasEx = pitches.some((p) => Object.keys(p.zones || {}).length);
      await api.post(`/events/${eventId}/exercises`, {
        session_pitches, session_target_duration: parseInt(targetDuration) || null,
        session_comment: sessionComment, session_note: sessionNote,
        session_groups: groups.filter((g) => g.label).map((g) => ({ label: g.label, member_ids: g.member_ids || [] })),
        session_ready: readyVal && hasEx,
      });
      setSessionReady(readyVal && hasEx);
      toast.success(readyVal && hasEx ? "Séance marquée comme PRÊTE" : "Séance enregistrée");
      await loadEvents();
      setEditMode(false);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Entraînements" subtitle="Cahier des exercices et préparation des séances."
        action={tab === "library" && (canExport || canManageEx) && (
          <div className="flex gap-2 flex-wrap">
            {canExport && <Button data-testid="export-exercises-pdf" variant="outline" onClick={() => downloadFile("/exercises/export.pdf", "cahier_exercices.pdf").catch(() => toast.error("Export échoué"))}><FileText size={16} /> PDF</Button>}
            {canManageEx && (
              <>
                <input id="import-pdf-input" data-testid="import-pdf-input" type="file" accept="application/pdf,.pdf" className="hidden" onChange={onImportPdf} disabled={importing} />
                <Button data-testid="import-pdf-btn" variant="outline" disabled={importing}
                  onClick={() => document.getElementById("import-pdf-input").click()}>
                  <FileUp size={16} /> {importing ? "Import en cours..." : "Importer un PDF"}
                </Button>
              </>
            )}
            {canManageEx && <Button data-testid="add-exercise-btn" onClick={openNew}><Plus size={16} /> Nouvel exercice</Button>}
          </div>
        )} />

      <div className="flex gap-2 mb-6 flex-wrap">
        <TabBtn active={tab === "library"} onClick={() => setTab("library")} icon={Dumbbell} label="Cahier des exercices" testid="tab-library" />
        <TabBtn active={tab === "sessions"} onClick={() => setTab("sessions")} icon={ClipboardList} label="Séances" testid="tab-sessions" />
        <TabBtn active={tab === "recap"} onClick={() => setTab("recap")} icon={LayoutList} label="Récapitulatif saison" testid="tab-recap" />
      </div>

      {tab === "library" ? (
        exercises.length ? (
          <>
            <div className="flex gap-2 mb-6 flex-wrap">
              <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>Tous</FilterChip>
              {THEMES.map((t) => (
                <FilterChip key={t} active={filter === t} onClick={() => setFilter(t)}>{t}</FilterChip>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {exercises.filter((ex) => filter === "all" || ex.theme === filter).map((ex) => (
              <Card key={ex.id} data-testid="exercise-card" onClick={() => openFile(`/exercises/${ex.id}/pdf`).catch(() => toast.error("Ouverture du PDF impossible"))} className="flex flex-col cursor-pointer hover:ring-2 hover:ring-primary/40 transition-shadow" title="Ouvrir la fiche PDF">
                {ex.image ? (
                  <img src={fileUrl(ex.image)} alt={ex.name} className="w-full h-36 object-cover rounded-lg mb-3 border border-border" />
                ) : (
                  <div className="w-full h-36 rounded-lg mb-3 bg-secondary flex items-center justify-center text-muted-foreground"><Dumbbell size={30} /></div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <p className="font-bold leading-tight">{ex.name}</p>
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`exercise-open-pdf-${ex.id}`} title="Ouvrir la fiche PDF"
                      onClick={(e) => { e.stopPropagation(); openFile(`/exercises/${ex.id}/pdf`).catch(() => toast.error("Ouverture impossible")); }}
                      className="p-1.5 text-muted-foreground hover:text-primary"><FileText size={14} /></button>
                    {canExport && <button data-testid={`exercise-pdf-btn-${ex.id}`} title="Télécharger la fiche PDF"
                      onClick={(e) => { e.stopPropagation(); downloadFile(`/exercises/${ex.id}/pdf`, `exercice_${ex.name}.pdf`).catch(() => toast.error("Export échoué")); }}
                      className="p-1.5 text-muted-foreground hover:text-primary"><Download size={14} /></button>}
                    {canManageEx && (
                      <>
                        <button data-testid="edit-exercise-btn" onClick={(e) => { e.stopPropagation(); openEdit(ex); }} className="p-1.5 text-muted-foreground hover:text-primary"><Pencil size={14} /></button>
                        <button data-testid="delete-exercise-btn" onClick={(e) => { e.stopPropagation(); remove(ex.id); }} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {ex.theme && <Badge color={THEME_COLOR[ex.theme] || "primary"}>{ex.theme}</Badge>}
                  {ex.duration ? <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} /> {ex.duration} min</span> : null}
                </div>
                {ex.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{ex.description}</p>}
                {(ex.material || ex.tasks || ex.group) && (
                  <div className="mt-3 pt-3 border-t border-border space-y-1.5 text-xs">
                    {ex.group && <p className="flex items-start gap-1.5 text-muted-foreground"><Users size={13} className="text-primary mt-0.5 shrink-0" /> <span><b className="text-foreground">Groupe :</b> {ex.group}</span></p>}
                    {ex.material && <p className="flex items-start gap-1.5 text-muted-foreground"><Package size={13} className="text-primary mt-0.5 shrink-0" /> <span><b className="text-foreground">Matériel :</b> {ex.material}</span></p>}
                    {ex.tasks && <p className="flex items-start gap-1.5 text-muted-foreground"><ListChecks size={13} className="text-primary mt-0.5 shrink-0" /> <span><b className="text-foreground">Tâches :</b> {ex.tasks}</span></p>}
                  </div>
                )}
              </Card>
            ))}
            </div>
          </>
        ) : <Empty text="Créez votre premier exercice pour construire le cahier." />
      ) : tab === "sessions" ? (
        <>
          <Card className="mb-6">
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Choisir une séance (entraînement)</label>
            <Select data-testid="session-select" value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="">Sélectionner...</option>
              {events.map((e) => <option key={e.id} value={e.id}>{fmtDate(e.date)} · {e.title}{e.category ? ` (${e.category})` : ""}</option>)}
            </Select>
          </Card>

          {eventId ? (
            exercises.length ? (
              <>
                {/* Barre d'actions */}
                {(() => {
                  const hasEx = pitches.some((p) => Object.keys(p.zones || {}).length);
                  const prep = !hasEx ? { label: "À préparer", color: "red" } : (sessionReady ? { label: "Prête", color: "green" } : { label: "En attente", color: "yellow" });
                  return (
                    <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                        {readOnly ? <Badge color="gray"><Lock size={12} /> Lecture seule</Badge> : <Badge color="green"><Pencil size={12} /> Édition</Badge>}
                        <Badge data-testid="session-prep-status" color={prep.color}>{prep.label}</Badge>
                        <span>{pitches.length} terrain(s) · {new Set(pitches.flatMap((p) => Object.values(p.zones))).size} exercice(s)</span>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {canPdf && savedSession && <Button data-testid="export-session-pdf" variant="outline" onClick={async () => { if (!readOnly) await saveSession(); downloadFile(`/events/${eventId}/exercises.pdf`, "seance.pdf").catch(() => toast.error("Export échoué")); }}><FileText size={15} /> PDF séance</Button>}
                        {canPlan && readOnly && <Button data-testid="edit-session-btn" onClick={() => setEditMode(true)}><Pencil size={15} /> Modifier</Button>}
                        {canPlan && !readOnly && <Button data-testid="generate-typical-session-btn" variant="outline" onClick={generateTypical}><Dumbbell size={15} /> Séance type</Button>}
                        {canPlan && !readOnly && <Button data-testid="save-session-btn" variant="outline" onClick={() => saveSession()} disabled={saving}><CheckCircle2 size={15} /> {saving ? "..." : "Enregistrer"}</Button>}
                        {canPlan && !readOnly && hasEx && !sessionReady && <Button data-testid="mark-ready-btn" onClick={() => saveSession(true)} disabled={saving}><CheckCircle2 size={15} /> Marquer comme prête</Button>}
                        {canPlan && !readOnly && sessionReady && <Button data-testid="unmark-ready-btn" variant="outline" onClick={() => saveSession(false)} disabled={saving}>Repasser en attente</Button>}
                      </div>
                    </div>
                  );
                })()}

                {!canPlan && <p className="text-xs text-muted-foreground mb-4 italic">Seul l'administrateur peut planifier ou modifier les séances. Vous consultez la séance en lecture seule.</p>}

                {/* Durée de la séance */}
                <Card className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-3" data-testid="session-summary">
                  <div className="flex items-center gap-2">
                    <Clock size={18} className="text-primary" />
                    <label className="text-sm font-bold">Durée de la séance :</label>
                    {readOnly ? <span className="text-sm font-bold text-primary">{targetDuration} min</span> : (
                      <input data-testid="session-target-duration" type="number" min="1" value={targetDuration ?? ""}
                        onChange={(e) => setTargetDuration(e.target.value === "" ? "" : Math.max(1, parseInt(e.target.value) || 0))}
                        className="w-20 h-8 px-2 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-primary" />
                    )}
                    {!readOnly && <span className="text-xs text-muted-foreground">min</span>}
                  </div>
                  <div className="h-5 w-px bg-border hidden sm:block" />
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Somme des terrains : <span data-testid="pitches-total" className={`font-bold ${pitchesTotal === parseInt(targetDuration) ? "text-primary" : "text-orange-500"}`}>{pitchesTotal} min</span></span>
                    {pitchesTotal === parseInt(targetDuration) ? <CheckCircle2 size={16} className="text-primary" /> : <span className="text-[11px] text-orange-500 font-semibold">≠ durée séance</span>}
                  </div>
                </Card>

                {/* Commentaire & note */}
                {(canPlan || sessionComment || sessionNote) && (
                  <Card className="mb-4" data-testid="session-notes-card">
                    <p className="font-semibold text-sm mb-3 flex items-center gap-2"><Pencil size={15} className="text-primary" /> Commentaire & note de la séance</p>
                    {readOnly ? (
                      <div className="space-y-2 text-sm">
                        <p><span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Commentaire : </span>{sessionComment || "—"}</p>
                        <p><span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Note interne : </span>{sessionNote || "—"}</p>
                      </div>
                    ) : (
                      <>
                        <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Commentaire de la séance du jour</label>
                        <textarea data-testid="session-comment" rows={2} value={sessionComment} onChange={(e) => setSessionComment(e.target.value)}
                          className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary mb-3" placeholder="Déroulé, ressenti, points à retravailler..." />
                        <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Note interne</label>
                        <textarea data-testid="session-note" rows={2} value={sessionNote} onChange={(e) => setSessionNote(e.target.value)}
                          className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary" placeholder="Note privée pour le staff..." />
                      </>
                    )}
                  </Card>
                )}

                {/* Groupes de joueurs (A, B, C...) */}
                {(canPlan || groups.length > 0) && (
                  <Card className="mb-4" data-testid="session-groups-card">
                    <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                      <p className="font-semibold text-sm flex items-center gap-2"><Users size={15} className="text-primary" /> Groupes de joueurs</p>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-muted-foreground font-semibold">Filtre</label>
                        <Select data-testid="group-grp-filter" value={grpFilter} onChange={(e) => setGrpFilter(e.target.value)} className="h-8 w-28">
                          <option value="all">Tous</option>
                          <option value="gr1">GRP 1</option>
                          <option value="gr2">GRP 2</option>
                        </Select>
                        {!readOnly && <Button data-testid="add-group-btn" variant="outline" onClick={addGroup}><Plus size={15} /> Ajouter un groupe</Button>}
                      </div>
                    </div>
                    {!readOnly && (
                      <p className="text-[11px] text-muted-foreground mb-3 italic">
                        Seuls les affiliés ayant confirmé leur participation à cette séance sont proposés{confirmedIds.size === 0 ? " — aucune confirmation pour l'instant." : ` (${confirmedIds.size} confirmé(s))`}. Un joueur déjà placé n'apparaît plus dans les autres groupes.
                      </p>
                    )}
                    {groups.length === 0 ? (
                      <Empty text={readOnly ? "Aucun groupe défini." : "Aucun groupe. Cliquez sur « Ajouter un groupe » (A, B, C...)."} />
                    ) : (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {groups.map((grp, i) => (
                          <div key={i} data-testid={`group-card-${i}`} {...dropProps(i)}
                            className={`rounded-lg border p-3 transition-colors ${dragOverGrp === i ? "border-primary bg-primary/10" : "border-border bg-secondary/30"}`}>
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="w-7 h-7 rounded-lg bg-primary/20 text-primary font-black flex items-center justify-center shrink-0">{grp.label}</span>
                                {!readOnly ? (
                                  <input data-testid={`group-label-${i}`} value={grp.label} onChange={(e) => renameGroup(i, e.target.value)}
                                    className="w-24 h-7 px-2 rounded bg-secondary border border-border text-xs font-bold outline-none focus:border-primary" />
                                ) : <span className="font-bold text-sm">Groupe {grp.label}</span>}
                                <Badge color="gray">{grp.member_ids.length}</Badge>
                              </div>
                              {!readOnly && <button data-testid={`remove-group-${i}`} onClick={() => removeGroup(i)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 size={15} /></button>}
                            </div>
                            {!readOnly && (
                              <Select data-testid={`group-add-member-${i}`} value="" onChange={(e) => { addMemberToGroup(i, e.target.value); e.target.value = ""; }} className="h-9 mb-2">
                                <option value="">+ Ajouter un joueur...</option>
                                {availableForGroup().map((m) => (
                                  <option key={m.id} value={m.id} style={m.admin_color ? { color: ADMIN_COLORS[m.admin_color] } : undefined}>
                                    {m.first_name} {m.last_name}{yearOf(m) ? ` (${yearOf(m)})` : ""}
                                  </option>
                                ))}
                              </Select>
                            )}
                            <div className="flex flex-wrap gap-1.5">
                              {grp.member_ids.length === 0 ? <span className="text-xs text-muted-foreground italic">Aucun joueur</span> :
                                grp.member_ids.map((mid) => {
                                  const m = memberById(mid);
                                  if (!m) return null;
                                  return (
                                    <span key={mid} data-testid={`group-${i}-member-${mid}`} {...dragProps(mid)}
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary border border-border text-xs font-semibold ${readOnly ? "" : "cursor-grab active:cursor-grabbing"}`}
                                      style={m.admin_color ? { color: ADMIN_COLORS[m.admin_color] } : undefined}>
                                      {m.first_name} {m.last_name}{yearOf(m) && <span className="text-muted-foreground font-normal">({yearOf(m)})</span>}
                                      {!readOnly && <button onClick={() => removeMemberFromGroup(i, mid)} className="text-muted-foreground hover:text-destructive"><X size={12} /></button>}
                                    </span>
                                  );
                                })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {!readOnly && groups.length > 0 && (
                      <div data-testid="group-unassigned-zone" {...dropProps(-1)}
                        className={`mt-3 rounded-lg border border-dashed p-3 transition-colors ${dragOverGrp === -1 ? "border-primary bg-primary/10" : "border-border"}`}>
                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Sans groupe · {availableForGroup().length} <span className="normal-case font-normal tracking-normal italic">— glissez un joueur ici pour le retirer de son groupe</span></p>
                        <div className="flex flex-wrap gap-1.5">
                          {availableForGroup().length === 0 ? <span className="text-xs text-muted-foreground italic">Aucun joueur</span> :
                            availableForGroup().map((m) => (
                              <span key={m.id} data-testid={`unassigned-member-${m.id}`} {...dragProps(m.id)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary border border-border text-xs font-semibold cursor-grab active:cursor-grabbing"
                                style={m.admin_color ? { color: ADMIN_COLORS[m.admin_color] } : undefined}>
                                {m.first_name} {m.last_name}{yearOf(m) && <span className="text-muted-foreground font-normal">({yearOf(m)})</span>}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}
                  </Card>
                )}

                {/* Terrains — LE TERRAIN EN PREMIER */}
                <div className="grid lg:grid-cols-[1fr_320px] gap-4 items-start">
                  <div className="space-y-4" data-testid="pitches-list">
                    {pitches.map((p) => {
                      const distinct = distinctOf(p);
                      const rot = distinct.length && parseInt(p.duration) ? Math.round(parseInt(p.duration) / distinct.length) : parseInt(p.duration) || 0;
                      return (
                        <Card key={p.uid} data-testid="pitch-card" className="border-primary/40">
                          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                            <div className="flex items-center gap-2">
                              <LayoutGrid size={16} className="text-primary" />
                              <span className="font-bold">{p.name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1.5">
                                <Clock size={14} className="text-primary" />
                                {readOnly ? <span className="text-sm font-bold text-primary">{p.duration} min</span> : (
                                  <input data-testid={`pitch-duration-${p.uid}`} type="number" min="1" value={p.duration ?? ""}
                                    onChange={(e) => setPitchDuration(p.uid, e.target.value === "" ? "" : Math.max(1, parseInt(e.target.value) || 0))}
                                    className="w-16 h-8 px-2 rounded-lg bg-secondary border border-border text-sm outline-none focus:border-primary" />
                                )}
                                <span className="text-xs text-muted-foreground">min</span>
                              </div>
                              {!readOnly && <button data-testid={`remove-pitch-${p.uid}`} onClick={() => removePitch(p.uid)} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>}
                            </div>
                          </div>
                          <PitchBoard zones={p.zones} exercises={exercises} editable={!readOnly}
                            onDropZone={(z, exId) => dropOnZone(p.uid, z, exId)} onClearZone={(z) => clearZone(p.uid, z)}
                            onZoneClick={readOnly ? (id) => setSheetEx(exercises.find((e) => e.id === id)) : undefined}
                            testid={`pitch-board-${p.uid}`} />
                          <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
                            {distinct.length > 0 ? (
                              <>
                                <Badge color="green">{distinct.length} exercice(s)</Badge>
                                {distinct.length > 1 && <span className="text-muted-foreground">Rotation ≈ <b className="text-foreground">{rot} min</b> / exercice</span>}
                                {distinct.map((exId) => <Badge key={exId} color="blue">{exercises.find((e) => e.id === exId)?.name || "?"}</Badge>)}
                              </>
                            ) : <span className="text-muted-foreground italic">{readOnly ? "Aucun exercice placé." : "Glissez des exercices du cahier dans les zones."}</span>}
                          </div>
                        </Card>
                      );
                    })}
                    {!readOnly && <Button data-testid="add-pitch-btn" variant="outline" className="w-full" onClick={addPitch}><Plus size={16} /> Ajouter un terrain</Button>}
                    {readOnly && pitches.length === 0 && <Empty text="Aucun terrain enregistré pour cette séance." />}
                  </div>

                  {/* Palette d'exercices à glisser */}
                  {!readOnly && (
                    <Card className="lg:sticky lg:top-4" data-testid="exercise-palette">
                      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><Dumbbell size={13} className="text-primary" /> Cahier des exercices</p>
                      <p className="text-[11px] text-muted-foreground mb-3">Glissez un exercice dans une zone du terrain. Un exercice peut occuper plusieurs zones voisines.</p>
                      <div className="flex gap-1.5 mb-3 flex-wrap">
                        <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>Tous</FilterChip>
                        {THEMES.map((t) => <FilterChip key={t} active={filter === t} onClick={() => setFilter(t)}>{t}</FilterChip>)}
                      </div>
                      <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1" data-testid="palette-exercises">
                        {exercises.filter((ex) => filter === "all" || ex.theme === filter).map((ex) => (
                          <div key={ex.id} data-testid={`palette-ex-${ex.id}`} draggable
                            onDragStart={(e) => e.dataTransfer.setData("text/plain", ex.id)}
                            className="flex items-center gap-2 py-2 px-3 rounded-lg bg-secondary/50 border border-border cursor-grab active:cursor-grabbing hover:border-primary/50 transition-colors">
                            <Dumbbell size={13} className="text-primary flex-shrink-0" />
                            <span className="font-semibold text-xs truncate flex-1">{ex.name}</span>
                            {ex.theme && <Badge color={THEME_COLOR[ex.theme] || "primary"}>{ex.theme}</Badge>}
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>
              </>
            ) : <Empty text="Ajoutez d'abord des exercices dans le cahier." />
          ) : <Empty text="Sélectionnez une séance pour préparer les terrains." />}
        </>
      ) : (
        recap.length ? (
          <div data-testid="recap-table">
            <div className="flex justify-end gap-2 mb-4">
              <Button data-testid="recap-export-pdf" variant="outline"
                onClick={() => downloadFile("/exercises/recap.pdf", "recap_entrainements.pdf").catch(() => toast.error("Export échoué"))}>
                <FileText size={16} /> PDF
              </Button>
              <Button data-testid="recap-export-xlsx" variant="outline"
                onClick={() => downloadFile("/exercises/recap.xlsx", "recap_entrainements.xlsx").catch(() => toast.error("Export échoué"))}>
                <LayoutList size={16} /> Sheet
              </Button>
            </div>
            <div className="space-y-6">
              {recap.map((grp) => (
                <div key={grp.theme} data-testid={`recap-theme-${grp.theme}`} className="rounded-xl border border-border overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-secondary/50 border-b border-border">
                    <Badge color={THEME_COLOR[grp.theme] || "primary"}>{grp.theme}</Badge>
                    <span className="text-xs text-muted-foreground">{grp.exercises.length} exercice(s)</span>
                  </div>
                  <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                          <th className="px-4 py-2 font-bold">Exercice</th>
                          <th className="px-4 py-2 font-bold">Groupe</th>
                          <th className="px-4 py-2 font-bold">Durée</th>
                          <th className="px-4 py-2 font-bold text-center">Nb réalisé</th>
                          <th className="px-4 py-2 font-bold">Dates des séances</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grp.exercises.map((ex) => (
                          <tr key={ex.id} data-testid="recap-exercise-row" className="border-b border-border/50 last:border-0 hover:bg-secondary/30">
                            <td className="px-4 py-3 font-semibold">{ex.name}</td>
                            <td className="px-4 py-3 text-muted-foreground">{ex.group || "—"}</td>
                            <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{ex.duration ? `${ex.duration} min` : "—"}</td>
                            <td className="px-4 py-3 text-center font-bold text-primary">{ex.count}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {ex.sessions.map((s) => (
                                  <span key={s.event_id} title={s.title}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-secondary text-xs font-semibold whitespace-nowrap">
                                    <CalendarDays size={11} className="text-primary" /> {fmtDate(s.date)}
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile cards */}
                  <div className="md:hidden divide-y divide-border/60">
                    {grp.exercises.map((ex) => (
                      <div key={ex.id} data-testid="recap-exercise-card" className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-sm min-w-0">{ex.name}</p>
                          <span className="font-bold text-primary shrink-0">×{ex.count}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{ex.group || "—"}{ex.duration ? ` · ${ex.duration} min` : ""}</p>
                        <div className="flex items-center gap-1.5 flex-wrap mt-2">
                          {ex.sessions.map((s) => (
                            <span key={s.event_id} title={s.title}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-secondary text-xs font-semibold whitespace-nowrap">
                              <CalendarDays size={11} className="text-primary" /> {fmtDate(s.date)}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : <Empty text="Aucun exercice réalisé pour le moment. Rattachez des exercices à vos séances." />
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Modifier l'exercice" : "Nouvel exercice"}>
        <form onSubmit={save} data-testid="exercise-form">
          <div className="flex items-center gap-4 mb-4">
            {form.image ? (
              <img src={fileUrl(form.image)} alt="schéma" className="w-20 h-16 rounded-lg object-cover border border-border" />
            ) : (
              <div className="w-20 h-16 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground"><Camera size={20} /></div>
            )}
            <label className="cursor-pointer">
              <span className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border text-sm font-bold hover:bg-secondary transition-colors">
                <Camera size={15} /> {uploading ? "Envoi..." : "Schéma / photo"}
              </span>
              <input data-testid="exercise-image-input" type="file" accept="image/*" className="hidden" onChange={onImg} disabled={uploading} />
            </label>
          </div>
          <Field label="Nom de l'exercice"><Input data-testid="exercise-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Passe et va" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Thème">
              <Select data-testid="exercise-theme" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })}>
                {THEMES.map((t) => <option key={t} value={t}>{t}</option>)}
              </Select>
            </Field>
            <Field label="Durée (min)"><Input data-testid="exercise-duration" type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} /></Field>
          </div>
          <Field label="Description / consignes">
            <textarea data-testid="exercise-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4} className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary" placeholder="Organisation, objectifs, variantes..." />
          </Field>
          <Field label="Matériel">
            <textarea data-testid="exercise-material" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })}
              rows={2} className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary" placeholder="Ex. 6 plots, 4 ballons, 2 mini-buts..." />
          </Field>
          <Field label="Tâches à réaliser">
            <textarea data-testid="exercise-tasks" value={form.tasks} onChange={(e) => setForm({ ...form, tasks: e.target.value })}
              rows={2} className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary" placeholder="Ex. réaliser 10 passes, marquer sur 3 touches..." />
          </Field>
          <Field label="Groupe">
            <Input data-testid="exercise-group" value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} placeholder="Ex. Groupe A, 4v4, par 2..." />
          </Field>
          <Button data-testid="save-exercise-btn" className="w-full mt-2">Enregistrer</Button>
        </form>
      </Modal>
      {sheetEx && <ExerciseSheetModal exercise={sheetEx} onClose={() => setSheetEx(null)} />}
    </div>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
    <Icon size={16} /> {label}
  </button>
);

const FilterChip = ({ active, children, ...props }) => (
  <button {...props} className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{children}</button>
);
